import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { StyledInput } from '../Input';

const Root = styled.div`
  width: 100%;
`;

const FieldRow = styled.div`
  position: relative;
  width: 100%;
`;

const InputWithTogglePadding = styled(StyledInput)`
  padding-right: ${rem(52)};
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  box-sizing: border-box;
  min-width: 44px;
  min-height: 44px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: #627779;
  border-radius: 4px;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid #2274a5;
    outline-offset: 2px;
  }
`;

const CapsHint = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: #627779;
  margin-top: ${rem(6)};
`;

const PasswordInput = props => {
  const { label, error, errorMessage, onKeyDown, onKeyUp, onBlur, autoComplete, ...rest } = props;
  const [visible, setVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const updateCapsLock = useCallback(e => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  }, []);

  const handleKeyDown = useCallback(
    e => {
      updateCapsLock(e);
      if (onKeyDown) onKeyDown(e);
    },
    [updateCapsLock, onKeyDown]
  );

  const handleKeyUp = useCallback(
    e => {
      updateCapsLock(e);
      if (onKeyUp) onKeyUp(e);
    },
    [updateCapsLock, onKeyUp]
  );

  const handleBlur = useCallback(
    e => {
      setCapsLockOn(false);
      if (onBlur) onBlur(e);
    },
    [onBlur]
  );

  return (
    <Root>
      <FieldRow>
        <InputWithTogglePadding
          {...rest}
          type={visible ? 'text' : 'password'}
          label={label}
          error={error}
          errorMessage={errorMessage}
          autoComplete={autoComplete}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onBlur={handleBlur}
        />
        <ToggleButton
          type="button"
          tabIndex={0}
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
          aria-pressed={visible}
          onClick={() => setVisible(v => !v)}
        >
          {visible ? (
            <MdVisibilityOff size={22} aria-hidden />
          ) : (
            <MdVisibility size={22} aria-hidden />
          )}
        </ToggleButton>
      </FieldRow>
      {capsLockOn ? (
        <CapsHint role="status" aria-live="polite">
          Caps Lock ativo
        </CapsHint>
      ) : null}
    </Root>
  );
};

export { PasswordInput };
